import React from "react";
import { Modal, Row, Col, Badge, Button } from "react-bootstrap";
import {
  FaStar,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGlobe,
  FaTrash,
  FaUser,
  FaEdit,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_SUPPLIER_IMAGE = "https://plus.unsplash.com/premium_photo-1701090939615-1794bbac5c06?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

const SupplierDetailsModal = ({ show, onHide, supplier, triggerPosition, onDeleteClick, onEditClick }) => {
  if (!supplier) return null;

  const handleEditClick = () => {
    onEditClick(supplier);
  };

  const handleDeleteClick = () => {
    onDeleteClick(supplier);
  };

  const handleImageError = (e) => {
    e.target.src = DEFAULT_SUPPLIER_IMAGE;
  };

  return (
    <AnimatePresence mode="wait">
      {show && (
        <Modal
          show={show}
          onHide={onHide}
          size="lg"
          centered
          className="supplier-modal"
          contentClassName="border-0 bg-transparent"
          backdropClassName="backdrop-blur"
          dir="rtl"
        >
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.8,
              x: triggerPosition?.x || 0,
              y: triggerPosition?.y || 0,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.8,
              x: triggerPosition?.x || 0,
              y: triggerPosition?.y || 0,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            className="bg-white rounded-4 shadow-lg overflow-hidden"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="position-relative">
                <img
                  src={supplier.imageUrl || DEFAULT_SUPPLIER_IMAGE}
                  alt={supplier.name}
                  onError={handleImageError}
                  style={{
                    width: "100%",
                    height: "300px",
                    objectFit: "cover",
                  }}
                />
                <div className="position-absolute bottom-0 start-0 p-4 bg-gradient-dark text-white" style={{ width: "100%" }}>
                  <div className="d-flex justify-content-between align-items-end">
                    <div className="text-end">
                      <h2 className="mb-0 fw-bold">{supplier.name}</h2>
                      <p className="mb-0 opacity-75">{supplier.category}</p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge
                        bg={supplier.isGolden ? "warning" : "primary"}
                        className="px-3 py-2 fs-5"
                      >
                        {supplier.rating} <FaStar className="me-1" />
                      </Badge>
                      {supplier.isPersonal && (
                        <Badge bg="info" className="px-3 py-2 fs-5">
                          <FaUser className="me-1" /> אישי
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <Row className="g-4">
                  <Col md={8}>
                    <h4 className="mb-3">על הספק</h4>
                    <p className="mb-0">{supplier.description}</p>

                    <div className="mt-4">
                      <h4 className="mb-3">תגיות</h4>
                      <div className="d-flex flex-wrap gap-2">
                        {supplier.tags.map((tag, index) => (
                          <Badge
                            key={index}
                            bg="light"
                            text="dark"
                            className="px-3 py-2"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Col>

                  <Col md={4}>
                    <div className="bg-light p-4 rounded-3">
                      <h4 className="mb-3">פרטי קשר</h4>
                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex align-items-center gap-2">
                          <FaPhone className="text-primary" />
                          <span>{supplier.phone}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <FaEnvelope className="text-primary" />
                          <span>{supplier.email}</span>
                        </div>
                        {supplier.address && (
                          <div className="d-flex align-items-center gap-2">
                            <FaMapMarkerAlt className="text-primary" />
                            <span>{supplier.address}</span>
                          </div>
                        )}
                        {supplier.website && (
                          <div className="d-flex align-items-center gap-2">
                            <FaGlobe className="text-primary" />
                            <a
                              href={supplier.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-break"
                            >
                              {supplier.website}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="mt-4 d-flex gap-2" style={{ justifyContent: "flex-end" }}>
                  {supplier.isPersonal && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="px-2 py-1"
                      onClick={handleEditClick}
                      style={{ width: "100px", margin: "0px" }}
                    >
                      <FaEdit className="me-1" /> ערוך
                    </Button>
                  )}
                  {supplier.isPersonal && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="px-2 py-1"
                      onClick={handleDeleteClick}
                      style={{ width: "100px", margin: "0px" }}
                    >
                      <FaTrash className="me-1" /> מחק
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default SupplierDetailsModal;
